<template>
  <div class="w-80">
    <h3 class="mb-6">{{ title }}</h3>

    <FormulateForm v-model="form" @submit="onSubmit">
      <div class="flex flex-col gap-4">
        <FormulateInput label="Name" name="name" validation="required" />

        <FormulateInput label="Job title" name="jobTitle" />

        <FormulateInput
          name="email"
          label="Email"
          validation="required|email"
          :disabled="isUpserting"
        />

        <FormulateInput
          v-if="!isUpserting"
          label="Password"
          type="password"
          name="password"
          validation="^required|min:8,length|matches:/[0-9]/"
          :validation-messages="{
            matches: 'Passwords require at least one number.',
          }"
        />

        <div class="flex flex-col gap-4 mt-3">
          <FormulateInput
            name="enableConnectionsEmails"
            type="checkbox"
            label="Receive Connections emails"
          />

          <FormulateInput
            name="enablePublicContact"
            type="checkbox"
            label="Display contact details publicly"
          />
        </div>

        <FormulateInput
          v-show="canShowRoles"
          name="role"
          type="select"
          label="Access level"
          placeholder=""
          validation="required"
          :options="ADMIN_ROLES"
        />

        <FormulateInput
          type="submit"
          :wrapper-class="['mt-4']"
          :input-class="['mb-0 group']"
          :disabled="isLoading"
        >
          <span :class="{ hidden: isLoading }">{{ submitText }}</span>
          <lottie
            :options="LOADING_ANIMATION_OPTIONS"
            :style="{ width: '1.5rem' }"
            :class="{ 'hidden': !isLoading, 'group-hover:hidden': isLoading }"
          />
          <lottie
            :options="LOADING_ANIMATION_OPTIONS_DARK"
            :style="{ width: '1.5rem' }"
            :class="{
              'hidden': !isLoading,
              'hidden group-hover:block': isLoading,
            }"
          />
        </FormulateInput>
      </div>
    </FormulateForm>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from '../../../../../js/store/index.js'
import { EventBus } from '../../../../../js/plugins/event-bus'
import {
  ADMIN_ROLES,
  LOADING_ANIMATION_OPTIONS,
  LOADING_ANIMATION_OPTIONS_DARK,
} from '../../../../../js/util/constants.js'

export default defineComponent({
  name: 'ModalUpsertAdmin',

  props: {
    config: { default: null },
  },

  setup(props) {
    const store = useStore()

    const isLoading = ref(false)
    const title = computed(() => {
      return isUpserting.value ? 'Edit Admin' : 'Add Admin'
    })
    const isUpserting = computed(() => !!adminId.value)
    const adminId = computed(() => props.config?.id || null)
    const admin = computed(() => {
      if (adminId.value) {
        return store.getters['shared/getAdmin'](adminId.value)
      }

      return null
    })

    const form = ref({
      name: null,
      email: null,
      password: null,
      jobTitle: null,
      enableConnectionsEmails: true,
      enablePublicContact: false,
    })

    const canShowRoles = computed(() => {
      // @TODO - Connections: implement roles!
      return false
    })

    const payload = computed(() => {
      if (isUpserting.value) {
        return {
          id: adminId.value,
          name: form.value.name,
          job_title: form.value.jobTitle,
          enable_connections_emails: form.value.enableConnectionsEmails,
          enable_public_contact: form.value.enablePublicContact,
        }
      }

      return {
        name: form.value.name,
        email: form.value.email,
        password: form.value.password,
        role: form.value.role,
        job_title: form.value.jobTitle,
        enable_connections_emails: form.value.enableConnectionsEmails,
        enable_public_contact: form.value.enablePublicContact,
      }
    })

    const submitText = computed(() => {
      return isUpserting.value ? 'Save' : 'Add'
    })
    const onSubmit = async () => {
      isLoading.value = true
      const admin = await store.dispatch(
        'careerwise/dispatchUpsertAdmin',
        payload.value,
      )
      isLoading.value = false

      if (admin) {
        EventBus.$emit('showModal', false)
        EventBus.$emit('reloadWebsiteSettings')
        EventBus.$emit('triggerAlertResponse', {
          status: 'success',
          message: isUpserting.value ? 'Admin udpated' : 'Admin added',
        })
      }
    }

    const init = () => {
      if (isUpserting.value) {
        form.value.name = admin.value.name
        form.value.email = admin.value.email
        form.value.jobTitle = admin.value.job_title
        form.value.enableConnectionsEmails =
          admin.value.enable_connections_emails || false
        form.value.enablePublicContact =
          admin.value.enable_public_contact || false
      }
    }

    onMounted(() => init())

    return {
      title,
      LOADING_ANIMATION_OPTIONS,
      LOADING_ANIMATION_OPTIONS_DARK,
      ADMIN_ROLES,
      isLoading,
      isUpserting,
      canShowRoles,
      form,
      submitText,
      onSubmit,
    }
  },
})
</script>
